import React, { useRef, useState } from 'react';
import { Avatar, Box, Button } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import Icon from '../../../assets/images/logos/avatar.png';
import { Dialog } from '../general/dialog/dialog';
import { asyncUpdateProfileImage } from '../../../services/users.service';
import { ToastSwal } from '../general/toastSwals/toastSwal';
import { RootState } from '../../../redux/store';
import { useSelector } from 'react-redux';
import { fileToBase64 } from '../../../shared/utils/files';

const ProfileImageModal = ({
    isOpen,
    setIsOpen,
    onClose,
}: {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onClose: () => void;
}) => {
    const { logUser } = useSelector((state: RootState) => state.general);
    const { id, profile_image } = logUser || {};
    const initialImage = profile_image || Icon;
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            const base64 = await fileToBase64(file);
            if (base64) setSelectedImage(base64.base64 as string);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleUpdateProfileImage = async () => {
        if (!selectedFile) return;

        const base64 = await fileToBase64(selectedFile);
        if (!base64) return;

        const payload = {
            base64: base64.base64 as string,
            originalname: selectedFile.name,
        };

        let res;
        try {
            res = await asyncUpdateProfileImage(id, payload);
        } catch (error) {}

        if (res) {
            ToastSwal.fire({
                text: '¡Felicidades! La imagen de perfil se actualizó satisfactoriamente.',
                icon: 'success',
                timer: 5000,
                position: 'center-end',
            });
            setIsOpen(false);
        } else {
            ToastSwal.fire({
                text: 'Lo sentimos, no se pudo actualizar la imagen de perfil. Por favor intenta nuevamente más tarde.',
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            title="Foto de perfil"
            textSecondBtn="Guardar"
            onClickSecondBtn={handleUpdateProfileImage}
            disabledSecondBtn={!selectedImage}
        >
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                p={3}
            >
                <Avatar
                    src={selectedImage || initialImage}
                    alt="Foto de perfil"
                    sx={{ width: 200, height: 200 }}
                />
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap={2}
                >
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ color: 'primary.main' }}
                    >
                        <UploadIcon /> Cargar
                    </Button>
                    <Button
                        onClick={handleRemoveImage}
                        sx={{ color: 'primary.main' }}
                        disabled={!selectedImage}
                    >
                        <DeleteIcon /> Quitar
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default ProfileImageModal;
